import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Skeleton,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Badge,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "../utils/axios-instance";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { ServerRoutes } from "../routes";
import { type AppPagination, SaleTransaction } from "../types";
import { useQuery } from "@tanstack/react-query";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoIcon from "@mui/icons-material/Info";

// Fetch Sale Transactions with Pagination and Date Filter
const fetchSaleTransactions = async (
  page: number,
  fromDate?: Dayjs | null,
  toDate?: Dayjs | null
) => {
  const query = new URLSearchParams();
  query.append("page", page.toString());
  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));

  const response = await axios.get<AppPagination<SaleTransaction>>(
    `${ServerRoutes.saleTransactions}?${query.toString()}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );

  return response.data;
};

export function SalesTransactionSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  
  // Filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(null);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(null);
  
  // Commission detail modal state
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<SaleTransaction | null>(null);

  // Query for fetching sale transactions
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useQuery<
    AppPagination<SaleTransaction>,
    Error
  >({
    queryKey: ["saleTransactions", page, fromDate?.format(), toDate?.format()], // Query key with page, fromDate, and toDate
    queryFn: () => fetchSaleTransactions(page, fromDate, toDate), // Fetch function
  });

  const openFilterModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const applyFilters = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setPage(1);
    closeFilterModal();
    refetch();
  };

  const clearFilters = () => {
    setTempFromDate(null);
    setTempToDate(null);
  };
  
  // Commission modal handlers
  const openCommissionModal = (transaction: SaleTransaction) => {
    setSelectedTransaction(transaction);
    setCommissionModalOpen(true);
  };
  
  const closeCommissionModal = () => {
    setCommissionModalOpen(false);
    setSelectedTransaction(null);
  };

  const hasActiveFilters = fromDate || toDate;
  
  // Calculate the number of active filters for badge
  const activeFilterCount = [
    fromDate ? 1 : 0,
    toDate ? 1 : 0
  ].reduce((sum, value) => sum + value, 0);

  // Handle removing filters directly from chips
  const handleRemoveFromDate = () => {
    setFromDate(null);
    setPage(1);
    refetch();
  };

  const handleRemoveToDate = () => {
    setToDate(null);
    setPage(1);
    refetch();
  };

  return (
    <Card 
      sx={{ 
        p: { xs: 2, sm: 4 },
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: 'white',
      }}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          justifyContent="space-between" 
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <Typography variant="h5" fontWeight="bold" color="text.secondary">
            Sale Transactions
          </Typography>
          <Badge 
            badgeContent={activeFilterCount} 
            color="primary" 
            invisible={!hasActiveFilters}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 'bold',
              }
            }}
          >
            <Button
              startIcon={<FilterListIcon />}
              variant={hasActiveFilters ? "contained" : "outlined"}
              color="primary"
              onClick={openFilterModal}
              sx={{ 
                height: '40px',
                borderRadius: 1.5,
              }}
            >
              Filters
            </Button>
          </Badge>
        </Box>

        {hasActiveFilters && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {fromDate && (
              <Chip 
                label={`From: ${fromDate.format('MMM DD, YYYY')}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
                onDelete={handleRemoveFromDate}
              />
            )}
            {toDate && (
              <Chip 
                label={`To: ${toDate.format('MMM DD, YYYY')}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
                onDelete={handleRemoveToDate}
              />
            )}
          </Stack>
        )}

        <Paper 
          elevation={0} 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {isError ? (
            <Box p={3} textAlign="center">
              <Typography color="error" variant="body1">
                🚨 {error?.message}
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box p={2}>
              {[...Array(4)].map((_, index) => (
                <Box key={index} display="flex" gap={2} py={2} px={2}>
                  <Skeleton variant="text" width="20%" height={30} />
                  <Skeleton variant="text" width="30%" height={30} />
                  <Skeleton variant="text" width="20%" height={30} />
                  <Skeleton variant="text" width="20%" height={30} />
                </Box>
              ))}
            </Box>
          ) : data?.items && data.items.length > 0 ? (
            <Box sx={{ 
              width: '100%',
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: isMobile ? '1200px' : '100%',
              }
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    {[
                      "ID",
                      "Amount",
                      "Discount",
                      "User ID",
                      "Username",
                      "Product",
                      "Commission",
                      "Date",
                      "Action",
                    ].map((value) => (
                      <TableCell
                        key={value}
                        sx={{ 
                          fontWeight: 600, 
                          color: 'text.secondary',
                          borderBottom: '1px solid #e0e0e0',
                          minWidth: value === 'Date' ? '200px' : value === 'Action' ? '80px' : '150px',
                        }}
                      >
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((transaction, index) => (
                    <TableRow
                      key={transaction.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {[
                        transaction.id,
                        transaction.amount,
                        transaction.discount,
                        transaction.user.id,
                        transaction.user.username,
                        transaction.product.name,
                        transaction.commission,
                      ].map((value, idx) => (
                        <TableCell 
                          key={idx}
                          sx={{ 
                            borderBottom: '1px solid #e0e0e0',
                            color: 'text.primary',
                            minWidth: '150px',
                          }}
                        >
                          {value}
                        </TableCell>
                      ))}
                      <TableCell 
                        sx={{ 
                          borderBottom: '1px solid #e0e0e0',
                          color: 'text.primary',
                          minWidth: '200px',
                        }}
                      >
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          borderBottom: '1px solid #e0e0e0',
                          minWidth: '80px',
                        }}
                      >
                        <Tooltip title="View Commission Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => openCommissionModal(transaction)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No transactions found.
              </Typography>
            </Box>
          )}
        </Paper>

        {data?.meta.totalPages && data.meta.totalPages > 1 && (
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={data.meta.totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Filter Modal */}
      <Dialog 
        open={isFilterModalOpen} 
        onClose={closeFilterModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Filter Sale Transactions
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Date Range
            </Typography>
            <Box display="flex" gap={2} flexDirection={isMobile ? "column" : "row"}>
              <DatePicker
                label="From Date"
                value={tempFromDate}
                onChange={(date) => setTempFromDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="To Date"
                value={tempToDate}
                onChange={(date) => setTempToDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={clearFilters}
            color="inherit"
          >
            Clear All
          </Button>
          <Button onClick={closeFilterModal} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={applyFilters} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commission Detail Modal */}
      <Dialog
        open={commissionModalOpen}
        onClose={closeCommissionModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Commission User Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransaction && (
            <Box display="flex" flexDirection="column" gap={2} py={1}>
              {selectedTransaction.commissionUser ? (
                <>
                  <Typography variant="body2">
                    ID: {selectedTransaction.commissionUser.id}
                  </Typography>
                  <Typography variant="body2">
                    Username: {selectedTransaction.commissionUser.username}
                  </Typography>
                  <Typography variant="body2">
                    Telegram ID: {selectedTransaction.commissionUser.telegramId}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  The user has no referrer
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={closeCommissionModal} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
